import { CheckCircle2, Star, Quote, Heart, Eye, Lightbulb, Award, Handshake, Users } from "lucide-react";

const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_financial-solutions-1/artifacts/6fzmai1n_My-Picture.png";

const values = [
  { icon: Heart, title: "Understanding" },
  { icon: Eye, title: "Transparency" },
  { icon: Lightbulb, title: "Finance Solutions" },
  { icon: Award, title: "Excellence" },
  { icon: Handshake, title: "Integrity" },
  { icon: Users, title: "Relationship" },
];

const testimonials = [
  {
    name: "Emily R",
    text: "I couldn't be happier with my choice to insure my home. Their coverage options were comprehensive, and they tailored the plan to meet my specific needs. The peace of mind I get knowing my home is well-protected is priceless.",
  },
  {
    name: "Mark S",
    text: "I had an amazing experience with KK Mortgage Solutions. The team was knowledgeable, patient, and truly committed to finding the best mortgage solution for my needs. Highly recommend them to anyone looking for a stress-free home-buying experience!",
  },
  {
    name: "Sophia L",
    text: "KK Mortgage Solutions made my home-buying journey seamless by offering both mortgage and home insurance services under one roof. Their team coordinated everything perfectly, saving me time and stress.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero Banner */}
      <section className="relative bg-[#111111] py-32 lg:py-40">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80')] bg-cover bg-center opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">About Us</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Your Partner in Achieving<br />
            <span className="text-[#d64241]">Homeownership Dreams</span>
          </h1>
        </div>
      </section>

      {/* Founder Message */}
      <section data-testid="founder-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
                Founder Message
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-black tracking-tight mb-6">
                Broker for the Firm
              </h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4 font-body">
                Hi, I'm Kunal Kapadia, the founder of KK Mortgage Solutions.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4 font-body">
                KKMS was born from my passion for making life easier for people to get on the
                property ladder and my desire to make the daunting process a lot easier and
                smoother having gone through it myself. My goal has always been to create
                something meaningful that genuinely helps people and stays true to our values of
                honesty and transparency.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4 font-body">
                Outside of work, I enjoy spending time with my family and friends as well as
                playing a little bit of badminton in my free time, which keeps me inspired and
                grounded.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed font-body">
                Thank you for visiting and for supporting what we're building here. I'm excited
                to have you with us on this journey.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
                <img
                  src={FOUNDER_IMAGE}
                  alt="Kunal Kapadia - Founder"
                  className="w-full h-[450px] lg:h-[550px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#111111] text-white rounded-2xl p-6 shadow-xl hidden lg:block">
                <p className="text-lg font-bold font-heading">Kunal Kapadia</p>
                <p className="text-sm font-body text-gray-400">Founder & Broker</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="values-section" className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
              Who We Are / Values
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black tracking-tight mb-6">
              Your Partner in Achieving Homeownership Dreams
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto font-body">
              At KK Mortgage Solutions, we are dedicated to simplifying the home financing journey
              for our clients. With years of expertise in the mortgage industry, we provide
              personalized solutions tailored to meet your unique needs and financial goals.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
            {values.map((value, i) => (
              <div
                key={value.title}
                data-testid={`value-card-${value.title.toLowerCase().replace(' ', '-')}`}
                className="value-card bg-white p-6 rounded-2xl border border-gray-100 text-center cursor-default"
              >
                <value.icon className="value-icon w-8 h-8 text-[#d64241] mx-auto mb-3 transition-colors duration-300" />
                <h3 className="text-sm font-semibold">{value.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="about-testimonials" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
              Testimonials
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-black tracking-tight">
              What People Say About Our Services
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div
                key={i}
                data-testid={`about-testimonial-${i}`}
                className="testimonial-card bg-gray-50 p-8 rounded-2xl border border-gray-100"
              >
                <Quote className="w-8 h-8 text-[#f4adad] mb-4" />
                <p className="text-gray-600 leading-relaxed mb-6 font-body text-sm italic">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#d64241] flex items-center justify-center text-white font-bold font-heading">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-black text-sm font-body">{t.name}</p>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="w-3 h-3 fill-[#d64241] text-[#d64241]" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
